import * as React from "react";
import t from "../../../i18n";
import type { NonEmptyArray } from "diagnostics-lib/array";
import { isNonEmptyArray } from "diagnostics-lib/array";
import ScreenGraph from "@ui/Picto/ScreenGraph";
import type { Project } from "../../../api/useProjects";
import { useProjectPeriod } from "../useProjectPeriod";
import QuantityLayout from "../Chart/QuantityLayout";
import QuantitySelector, { QuantitySelectorType } from "../QuantitySelector";
import type { SelectedQuantity } from "../useSelectedQuantities";
import useSelectedQuantities from "../useSelectedQuantities";
import SymptomEvolution from "./SymptomEvolution";
import type { Source } from "../../../api/useSources";
import { useProjectSource } from "../../../api/useSources";
import useSelectedAnalyses from "../Analysis/useSelectedAnalyses";
import Loading from "@ui/Loading";

import { useNavigate, useOutletContext } from "react-router";
import BlankStateV2 from "@ui/BlankStateV2";
import { useSymptomChartType } from "./hook/useSymptomChartType";
import type { Quantity } from "../../../api/useQuantities";
import { toPath } from "../../../router/helpers/path";
import useActiveAnalysesWithDisplayFilters from "../../../api/useActiveAnalysesWithDisplayFilters";

interface PrivateP {
  project: Project;
  source: Source;
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
}

const ProjectSymptoms = ({ project, source, selectedQuantities }: PrivateP) => {
  const navigate = useNavigate();
  const projectId = project.id;
  const { displayFilterRemovesAllActiveAnalyses, activeAnalyses } =
    useActiveAnalysesWithDisplayFilters(project.id);
  const { selectedAnalyses } = useSelectedAnalyses(projectId, activeAnalyses);

  const [_, _setSelectedQuantities] = useSelectedQuantities(project.id);
  const [selectedChartType, setSelectedChartType] = useSymptomChartType({
    scope: projectId,
    defaultValue: "amplitude",
  });
  const [period] = useProjectPeriod({
    source,
    project,
    analyses: activeAnalyses,
    boundaries: "active-analyses",
  });

  const setSelectedQuantities = React.useCallback(
    (newQuantities: Quantity[]) => {
      if (
        newQuantities.length > 1 &&
        selectedQuantities.length < 2 &&
        selectedChartType !== "normalizedGap"
      ) {
        setSelectedChartType("normalizedGap");
      }

      _setSelectedQuantities(newQuantities);
    },
    [_setSelectedQuantities, selectedChartType, selectedQuantities.length, setSelectedChartType],
  );

  React.useEffect(() => {
    if (selectedQuantities.length > 1 && selectedChartType !== "normalizedGap") {
      setSelectedChartType("normalizedGap");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const quantitySelector = React.useMemo(
    () => (
      <QuantitySelector
        timezone={project.timezone}
        projectId={projectId}
        onlyFirstDisplayed={selectedChartType !== "normalizedGap"}
        selectedQuantities={selectedQuantities}
        setSelectedQuantities={setSelectedQuantities}
        activeAnalyses={activeAnalyses}
        selectedAnalyses={selectedAnalyses}
        alertType={QuantitySelectorType.Symptoms}
        page={QuantitySelectorType.Symptoms}
        period={period}
        allAnalysesMode={false}
      />
    ),
    [
      projectId,
      selectedChartType,
      selectedQuantities,
      setSelectedQuantities,
      activeAnalyses,
      selectedAnalyses,
      period,
      project.timezone,
    ],
  );

  if ((!isNonEmptyArray(activeAnalyses) && !displayFilterRemovesAllActiveAnalyses) || !source)
    return (
      <BlankStateV2
        picto={<ScreenGraph />}
        title={t("common.empty.activeAnalyses.title")}
        subtitle={t("common.empty.activeAnalyses.description")}
        onClick={() =>
          navigate(
            toPath("/projects/:projectId/measures", {
              projectId: project.id,
            }),
          )
        }
        buttonLabel={t("common.empty.activeAnalyses.action")}
      />
    );

  return (
    <QuantityLayout quantities={quantitySelector}>
      <SymptomEvolution
        project={project}
        source={source}
        activeAnalyses={activeAnalyses}
        selectedQuantities={selectedQuantities}
        selectedChartOption={selectedChartType}
        setSelectedChartOption={setSelectedChartType}
      />
    </QuantityLayout>
  );
};

export const ProjectSymptomsNonIdealProxy = () => {
  const project = useOutletContext<Project>();
  if (!project) throw new Error("no project");

  const projectId = project.id;
  const [selectedQuantities] = useSelectedQuantities(projectId);
  const source = useProjectSource(project);
  if (source === null) return <Loading />; // Will happen temporarily if source is deletable before project gets removed as well or if project is created before source get a chance to be synced

  if (!isNonEmptyArray(selectedQuantities))
    return (
      <BlankStateV2
        title={t("measures.empty.title")}
        subtitle={t("commons.no_quantities_yet.text")}
        picto={<ScreenGraph />}
      />
    );

  return (
    <ProjectSymptoms project={project} source={source} selectedQuantities={selectedQuantities} />
  );
};
